import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Modal,
  Button,
  Form,
  Table,
  Spinner,
  Alert,
  Card,
  Badge,
} from "react-bootstrap";
import {
  clientService,
  projectDropdownService,
  kpiAPI,
  kpiService,
} from "../services/apiService";
import Layout from "../components/Layout.jsx";
import { useAuth } from "../context/AuthContext"; // [EDITED: KPI Task - Mahesh Sharma]
import { isKPILockedFrontend } from "../utils/kpiLockout"; // [EDITED: KPI Task - Mahesh Sharma]
import { formatFYWeekLabel } from "../utils/fyWeekUtils"; // [EDITED: KPI Task - Mahesh Sharma]
import "./EmployeeKpi.css";

// Helper function to convert week code (YYYY-Www) to date range
const getWeekDateRange = (weekCode) => {
  if (!weekCode) return { start: null, end: null };

  const [year, week] = weekCode.split("-W");
  if (!year || !week) return { start: null, end: null };

  // Calculate Monday of the week
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const monday = new Date(simple);
  if (dow <= 4) monday.setDate(simple.getDate() - simple.getDay() + 1);
  else monday.setDate(simple.getDate() + 8 - simple.getDay());

  // Calculate Sunday of the week
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  return {
    start: monday,
    end: sunday,
  };
};

const getCurrentWeekCode = () => {
  const today = new Date();

  const firstJan = new Date(today.getFullYear(), 0, 1);
  const days = Math.floor(
    (today - firstJan) / (24 * 60 * 60 * 1000)
  );

  const week = Math.ceil((days + firstJan.getDay() + 1) / 7);

  return `${today.getFullYear()}-W${String(week).padStart(2, "0")}`;
};

// Format date to YYYY-MM-DD for input min/max
const formatDateForInput = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  return formatDateForInput(today);
};

// Constants
const INITIAL_FORM_STATE = {
  client: "",
  project: "",
  description: "",
  tat: "",
  achieved: "",
  remarks: "",
};

const EmployeeKpi = () => {
  const { user } = useAuth(); // [EDITED: KPI Task - Mahesh Sharma]
  const navigate = useNavigate();
  const location = useLocation();

  // Create refs for date inputs
  const tatRefs = useRef([]);
  const achievedRefs = useRef([]);

  // State management
  const [forms, setForms] = useState([{ ...INITIAL_FORM_STATE }]);
  const [selectedWeek, setSelectedWeek] = useState(() => {
    // Prefer week from query string, then location.state, then blank
    const params = new URLSearchParams(location.search);
    return params.get("week") || location.state?.week || "";
  });

  const isLocked = isKPILockedFrontend(selectedWeek, user?.role); // [EDITED: KPI Task - Mahesh Sharma]

  const weekDateRange = useMemo(
    () => getWeekDateRange(selectedWeek),
    [selectedWeek],
  );
  
  const achievedDateLimits = useMemo(() => {
    if (!weekDateRange.start || !weekDateRange.end) {
      return { min: "", max: "" };
    }

    const start = new Date(weekDateRange.start);
    const end = new Date(weekDateRange.end);
    const today = new Date();

    start.setHours(0,0,0,0);
    end.setHours(0,0,0,0);
    today.setHours(0,0,0,0);

    const currentWeek = getCurrentWeekCode();

    let maxDate;

    // ✅ If selected week is CURRENT WEEK
    if (selectedWeek === currentWeek) {
      maxDate = today;
    } 
    // ✅ If selected week is past week
    else {
      maxDate = end;
    }

    return {
      min: formatDateForInput(start),
      max: formatDateForInput(maxDate),
    };
  }, [weekDateRange, selectedWeek]);
  
  // Get week start date (Monday) as min date for achieved
  const weekStartDate = weekDateRange.start
    ? formatDateForInput(weekDateRange.start)
    : "";
  
  // Get today's date as max date for achieved
  const todayDate = getTodayDate();

  // Keep selectedWeek in sync with query string
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const weekParam = params.get("week");
    if (weekParam && weekParam !== selectedWeek) {
      setSelectedWeek(weekParam);
    }
  }, [location.search]);
  
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState({
    clients: false,
    projects: false,
    submission: false,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [notification, setNotification] = useState({
    show: false,
    type: "",
    message: "",
  });

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        setLoading((prev) => ({ ...prev, clients: true, projects: true }));

        const [clientsResponse, projectsResponse] = await Promise.all([
          clientService.getClients().catch(() => ({ data: [] })),
          projectDropdownService.getProjects().catch(() => []),
        ]);

        setClients(
          Array.isArray(clientsResponse?.data)
            ? clientsResponse.data
            : Array.isArray(clientsResponse)
              ? clientsResponse
              : [],
        );
        setProjects(Array.isArray(projectsResponse) ? projectsResponse : []);
      } catch (error) {
        showNotification(
          "danger",
          "Failed to load dropdown data. Please refresh the page.",
        );
      } finally {
        setLoading((prev) => ({ ...prev, clients: false, projects: false }));
      }
    };

    fetchDropdownData();
  }, []);

  // Utility functions
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
  };

  const hideNotification = () => {
    setNotification({ show: false, type: "", message: "" });
  };

  const resetForm = useCallback(() => {
    setForms([{ ...INITIAL_FORM_STATE }]);
    setErrors({});
    setTouched({});
    setShowModal(false);
  }, []);

  // Validation - Updated to make remarks non-mandatory and modify date validation
  const validateForm = useCallback((formData, index) => {
    const errors = {};

    if (!formData.client?.trim()) errors.client = "Client is required";
    if (!formData.project?.trim()) errors.project = "Project is required";
    if (!formData.description?.trim())
      errors.description = "Description is required";
    if (!formData.tat) errors.tat = "Target date is required";
    if (!formData.achieved) errors.achieved = "Achieved date is required";
    // Remarks validation removed - now optional

    // Date validation - Achieved date must be within current week and not in future
    if (formData.achieved && weekDateRange.start && weekDateRange.end) {
      const achievedDate = new Date(formData.achieved);
      const weekStart = new Date(weekDateRange.start);
      const weekEnd = new Date(weekDateRange.end);
      const today = new Date();
      
      // Reset hours to compare dates only
      weekStart.setHours(0, 0, 0, 0);
      weekEnd.setHours(23, 59, 59, 999);
      achievedDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      
      // Check if achieved date is within the current week
      if (achievedDate < weekStart || achievedDate > weekEnd) {
        errors.achieved = `Achieved date must be within week ${selectedWeek}`;
      }
      
      // Check if achieved date is not in the future
      if (achievedDate > today) {
        errors.achieved = "Achieved date cannot be in the future";
      }
    }

    // Note: Target date (tat) has no date restrictions - can be any date

    return errors;
  }, [weekDateRange, selectedWeek]);

  const validateAllForms = useCallback(() => {
    const allErrors = {};
    let isValid = true;

    forms.forEach((form, index) => {
      const formErrors = validateForm(form, index);
      if (Object.keys(formErrors).length > 0) {
        allErrors[index] = formErrors;
        isValid = false;
      }
    });

    setErrors(allErrors);
    return isValid;
  }, [forms, validateForm]);

  // Event handlers
  const handleChange = useCallback(
    (idx, e) => {
      const { name, value } = e.target;

      setForms((prev) => {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], [name]: value };
        return updated;
      });

      // Clear field error on change
      if (errors[idx]?.[name]) {
        setErrors((prev) => ({
          ...prev,
          [idx]: { ...prev[idx], [name]: undefined },
        }));
      }
    },
    [errors],
  );

  const handleBlur = useCallback((idx, field) => {
    setTouched((prev) => ({
      ...prev,
      [`${idx}-${field}`]: true,
    }));
  }, []);

  const handleAddKpiRow = useCallback(() => {
    setForms((prev) => [...prev, { ...INITIAL_FORM_STATE }]);
  }, []);

  const handleRemoveKpiRow = useCallback(
    (idx) => {
      if (forms.length === 1) {
        showNotification("warning", "At least one KPI is required");
        return;
      }

      setForms((prev) => prev.filter((_, i) => i !== idx));
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[idx];
        return newErrors;
      });
    },
    [forms.length],
  );

  // Improved date picker handler using refs
  const openDatePicker = (ref) => {
    if (ref && ref.current) {
      ref.current.showPicker();
    }
  };

  // API handlers
  const handleSaveDraft = async (e) => {
    e.preventDefault();
    try {
      setLoading((prev) => ({ ...prev, submission: true }));
      
      // NEW: Use batch submission for drafts as well
      // Map all forms to draft KPIs with isDraft flag
      const draftsData = forms.map((form) => {
        let projectName = "";
        if (form.project === "Others") {
          projectName = form.project_other || "";
        } else {
          const projectObj = projects.find(
            (p) => String(p.project_id) === String(form.project),
          );
          projectName = projectObj?.project_name || "";
        }
        const { project, project_other, ...rest } = form;
        const payload = {
          ...rest,
          projectName,
          week_selected: selectedWeek, // Match backend field name
          isDraft: true, // Mark as draft
        };
        return payload;
      });

      // Submit all drafts in a single batch request
      await kpiService.submitBatchKPIs(draftsData); // POST /api/kpi/submit-batch with { kpis: [...] }
      
      // Show centered success notification
      showNotification(
        "success",
        `KPI${forms.length > 1 ? "s" : ""} saved as draft successfully!`,
      );
      
      resetForm();
      
      // Navigate back after 1.5 seconds
      setTimeout(() => {
        hideNotification();
        navigate(-1);
      }, 1500);
      
    } catch (err) {
      showNotification("danger", "Failed to save draft. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, submission: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLocked) { // [EDITED: KPI Task - Mahesh Sharma]
      showNotification("danger", "KPI submissions are locked at 10:00 AM every Monday for this period."); // [EDITED: KPI Task - Mahesh Sharma]
      return; // [EDITED: KPI Task - Mahesh Sharma]
    } // [EDITED: KPI Task - Mahesh Sharma]
    if (!validateAllForms()) {
      showNotification(
        "danger",
        "Please fill in all required fields correctly.",
      );
      return;
    }
    try {
      setLoading((prev) => ({ ...prev, submission: true }));
      
      // NEW: Use batch submission instead of parallel individual requests
      // This ensures only ONE email is sent with ALL KPI details in a single table
      const kpisData = forms.map((form) => {
        let projectName = "";
        if (form.project === "Others") {
          projectName = form.project_other || "";
        } else {
          const projectObj = projects.find(
            (p) => String(p.project_id) === String(form.project),
          );
          projectName = projectObj?.project_name || "";
        }
        const { project, project_other, ...rest } = form;
        const payload = {
          ...rest,
          projectName,
          week_selected: selectedWeek, // Match backend field name
        };
        return payload;
      });

      // Submit all KPIs in a single batch request
      await kpiService.submitBatchKPIs(kpisData); // POST /api/kpi/submit-batch with { kpis: [...] }
      
      // Show centered success notification
      showNotification(
        "success",
        `KPI${forms.length > 1 ? "s" : ""} submitted for review successfully!`,
      );
      
      resetForm();
      
      // Navigate back after 1.5 seconds
      setTimeout(() => {
        hideNotification();
        navigate(-1);
      }, 1500);
      
    } catch (err) {
      showNotification("danger", "Failed to submit KPI(s). Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, submission: false }));
    }
  };

  // Memoized values
  const projectOptions = useMemo(() => {
    return projects.map((p) => ({
      value: p.project_id,
      label: p.project_name,
    }));
  }, [projects]);

  const clientOptions = useMemo(() => {
    return clients.map((c) => ({
      value: c.customer_id,
      label: c.customer_name,
    }));
  }, [clients]);

  return (
    <Layout>
      <main id="main" className="main employee-kpi-page">
        <div className="container mt-4">
          {/* Industry Standard Centered Popup Notification */}
          {notification.show && (
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 9999,
                width: "400px",
                backgroundColor: "#fff",
                borderRadius: "12px",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.05)",
                border: "none",
                overflow: "hidden",
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
              }}
            >
              <div
                style={{
                  height: "8px",
                  width: "100%",
                  backgroundColor: 
                    notification.type === "success" ? "#10b981" :
                    notification.type === "danger" ? "#ef4444" :
                    notification.type === "warning" ? "#f59e0b" : "#3b82f6",
                }}
              />
              
              <div style={{ padding: "24px 24px 20px 24px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: 
                        notification.type === "success" ? "#ecfdf5" :
                        notification.type === "danger" ? "#fef2f2" :
                        notification.type === "warning" ? "#fffbeb" : "#eff6ff",
                      flexShrink: 0,
                    }}
                  >
                    {notification.type === "success" && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    {notification.type === "danger" && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6L18 18" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    {notification.type === "warning" && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 9V13M12 17H12.01M5.07183 3H18.9282C20.4678 3 21.4301 4.64845 20.6603 5.98107L13.7321 18.243C12.962 19.5757 11.038 19.5757 10.2679 18.243L3.33975 5.98107C2.56992 4.64845 3.53218 3 5.07183 3Z" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    {notification.type === "info" && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3
                      style={{
                        margin: "0 0 4px 0",
                        fontSize: "18px",
                        fontWeight: "600",
                        color: "#111827",
                        lineHeight: "1.4",
                      }}
                    >
                      {notification.type === "success" && "Success"}
                      {notification.type === "danger" && "Error"}
                      {notification.type === "warning" && "Warning"}
                      {notification.type === "info" && "Information"}
                    </h3>
                    <p
                      style={{
                        margin: "0",
                        fontSize: "14px",
                        color: "#6b7280",
                        lineHeight: "1.5",
                      }}
                    >
                      {notification.message}
                    </p>
                  </div>
                </div>
                <div
                  style={{
                    marginTop: "20px",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={hideNotification}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#374151",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      outline: "none",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#f3f4f6";
                      e.target.style.borderColor = "#d1d5db";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#f9fafb";
                      e.target.style.borderColor = "#e5e7eb";
                    }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white border-0 pt-4 pb-2">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(-1)}
                  style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "0.5rem",
                    padding: "0.375rem 1rem",
                    fontSize: "0.875rem"
                  }}
                >
                  <i className="bi bi-arrow-left"></i> Back
                </button>
              </div>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h1 className="h3 mb-1 fw-bold" style={{ color: "#76df23" }}>
                    My KPIs
                  </h1>
                  <p className="text-muted mb-0">
                    Add and manage your Key Performance Indicators
                  </p>
                </div>
                {selectedWeek && (
                  <Badge
                    bg="success"
                    text="light"
                    className="px-3 py-2 ms-3"
                    style={{
                      fontSize: "1rem",
                      fontWeight: 500,
                      borderRadius: "20px",
                    }}
                  >
                    <i className="bi bi-calendar-week me-2"></i>
                    {formatFYWeekLabel(selectedWeek)} {/* [EDITED: KPI Task - Mahesh Sharma] */}
                  </Badge>
                )}
              </div>
            </Card.Header>

            <Card.Body className="p-4">
              {isLocked && ( // [EDITED: KPI Task - Mahesh Sharma]
                <Alert variant="warning" className="d-flex align-items-center mb-4">
                  <i className="bi bi-lock-fill me-2 fs-5"></i>
                  <div>
                    <strong>KPI Submissions Locked:</strong> Submissions and edits for previous weeks are locked on Mondays.
                  </div>
                </Alert>
              )}
              <Form onSubmit={handleSubmit}>
                {/* KPI Forms */}

                {forms.map((form, idx) => {
                  const formErrors = errors[idx] || {};
                  
                  // Initialize refs for this row if they don't exist
                  if (!tatRefs.current[idx]) {
                    tatRefs.current[idx] = React.createRef();
                  }
                  if (!achievedRefs.current[idx]) {
                    achievedRefs.current[idx] = React.createRef();
                  }
                  
                  return (
                    <Card
                      key={idx}
                      className={`mb-4 border ${forms.length > 1 ? "border-primary" : ""}`}
                      style={{ backgroundColor: "#fafafa" }}
                    >
                      <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3">
                        <div className="d-flex align-items-center">
                          <span className="fw-bold text-primary">
                            KPI #{idx + 1}
                          </span>
                          {/* Removed 'Additional' badge as requested */}
                        </div>
                        {forms.length > 1 && idx > 0 && (
                          <Button
                            variant="link"
                            className="text-danger p-0"
                            onClick={() => handleRemoveKpiRow(idx)}
                            aria-label="Remove KPI"
                          >
                            <i className="bi bi-trash fs-5"></i>
                          </Button>
                        )}
                      </Card.Header>
                      <Card.Body>
                        {/* Row 1: Client, Project, Target Date, Achieved Date */}
                        <div className="row g-3 mb-3">
                          <div className="col-md-3">
                            <Form.Group>
                              <Form.Label className="fw-semibold">
                                Client Name{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Select
                                name="client"
                                value={form.client}
                                onChange={(e) => handleChange(idx, e)}
                                onBlur={() => handleBlur(idx, "client")}
                                isInvalid={
                                  touched[`${idx}-client`] &&
                                  !!formErrors.client
                                }
                                disabled={loading.clients}
                                required
                              >
                                <option value="">Select Client</option>
                                {clientOptions.map(({ value, label }) => (
                                  <option key={value} value={value}>
                                    {label}
                                  </option>
                                ))}
                                <option value="Others">Others</option>
                              </Form.Select>
                              {form.client === "Others" && (
                                <Form.Control
                                  type="text"
                                  name="client_other"
                                  value={form.client_other || ""}
                                  onChange={(e) => handleChange(idx, e)}
                                  placeholder="Enter client name"
                                  className="mt-2"
                                  required
                                />
                              )}
                              <Form.Control.Feedback type="invalid">
                                {formErrors.client}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </div>
                          <div className="col-md-3">
                            <Form.Group>
                              <Form.Label className="fw-semibold">
                                Project Name{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Select
                                name="project"
                                value={form.project}
                                onChange={(e) => handleChange(idx, e)}
                                onBlur={() => handleBlur(idx, "project")}
                                isInvalid={
                                  touched[`${idx}-project`] &&
                                  !!formErrors.project
                                }
                                disabled={loading.projects}
                                required
                              >
                                <option value="">Select Project</option>
                                {projectOptions.map(({ value, label }) => (
                                  <option key={value} value={value}>
                                    {label}
                                  </option>
                                ))}
                                <option value="Others">Others</option>
                              </Form.Select>
                              {form.project === "Others" && (
                                <Form.Control
                                  type="text"
                                  name="project_other"
                                  value={form.project_other || ""}
                                  onChange={(e) => handleChange(idx, e)}
                                  placeholder="Enter project name"
                                  className="mt-2"
                                  required
                                />
                              )}
                              <Form.Control.Feedback type="invalid">
                                {formErrors.project}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </div>
                          <div className="col-md-3">
                            <Form.Group>
                              <Form.Label className="fw-semibold">
                                Target Date{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <div 
                                className="date-picker-wrapper"
                                onClick={() => openDatePicker(tatRefs.current[idx])}
                                style={{ cursor: 'pointer', position: 'relative' }}
                              >
                                <Form.Control
                                  type="date"
                                  name="tat"
                                  value={form.tat}
                                  onChange={(e) => handleChange(idx, e)}
                                  onBlur={() => handleBlur(idx, "tat")}
                                  isInvalid={
                                    touched[`${idx}-tat`] && !!formErrors.tat
                                  }
                                  required
                                  ref={tatRefs.current[idx]}
                                  style={{ paddingRight: '30px' }}
                                />
                                <i 
                                  className="bi bi-calendar3 date-picker-icon" 
                                  style={{ 
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    pointerEvents: 'none',
                                    color: '#6c757d'
                                  }}
                                ></i>
                              </div>
                              <Form.Control.Feedback type="invalid">
                                {formErrors.tat}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </div>
                          <div className="col-md-3">
                            <Form.Group>
                              <Form.Label className="fw-semibold">
                                Achieved Date{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <div 
                                className="date-picker-wrapper"
                                onClick={() => openDatePicker(achievedRefs.current[idx])}
                                style={{ cursor: 'pointer', position: 'relative' }}
                              >
                                <Form.Control
                                  type="date"
                                  name="achieved"
                                  value={form.achieved}
                                  onChange={(e) => handleChange(idx, e)}
                                  onBlur={() => handleBlur(idx, "achieved")}
                                  isInvalid={
                                    touched[`${idx}-achieved`] &&
                                    !!formErrors.achieved
                                  }
                                  required
                                  ref={achievedRefs.current[idx]}
                                  min={achievedDateLimits.min}
                                  max={achievedDateLimits.max}
                                  style={{ paddingRight: '30px' }}
                                />
                                <i 
                                  className="bi bi-calendar3 date-picker-icon" 
                                  style={{ 
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    pointerEvents: 'none',
                                    color: '#6c757d'
                                  }}
                                ></i>
                              </div>
                              <Form.Control.Feedback type="invalid">
                                {formErrors.achieved}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </div>
                        </div>
                        {/* Row 2: Description and Remarks */}
                        <div className="row g-3">
                          <div className="col-md-6">
                            <Form.Group>
                              <Form.Label className="fw-semibold">
                                Description{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                name="description"
                                value={form.description}
                                onChange={(e) => handleChange(idx, e)}
                                onBlur={() => handleBlur(idx, "description")}
                                isInvalid={
                                  touched[`${idx}-description`] &&
                                  !!formErrors.description
                                }
                                rows={4}
                                placeholder="Describe the KPI in detail..."
                                required
                              />
                              <Form.Control.Feedback type="invalid">
                                {formErrors.description}
                              </Form.Control.Feedback>
                              <Form.Text className="text-muted">
                                Max 500 characters
                              </Form.Text>
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group>
                              <Form.Label className="fw-semibold">
                                Remarks{/* Removed asterisk as it's no longer required */}
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                name="remarks"
                                value={form.remarks}
                                onChange={(e) => handleChange(idx, e)}
                                onBlur={() => handleBlur(idx, "remarks")}
                                isInvalid={
                                  touched[`${idx}-remarks`] &&
                                  !!formErrors.remarks
                                }
                                rows={4}
                                placeholder="Add any additional remarks (optional)..."
                                // Removed required attribute
                              />
                              <Form.Control.Feedback type="invalid">
                                {formErrors.remarks}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  );
                })}

                {/* Add KPI Button */}
                <div className="d-flex justify-content-between align-items-center mt-3 mb-4">
                  <Button
                    variant="outline-primary"
                    onClick={handleAddKpiRow}
                    className="d-flex align-items-center"
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Add Another KPI
                  </Button>

                  <div className="text-muted">
                    <small>Total KPIs: {forms.length}</small>
                  </div>
                </div>

                {/* Action Buttons - No spinners */}
                <div className="d-flex gap-3 justify-content-end border-top pt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={handleSaveDraft}
                    className="px-4"
                    disabled={loading.submission}
                  >
                    {loading.submission ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2"></i>
                        Save as Draft
                      </>
                    )}
                  </Button>

                  <Button
                    variant="success"
                    type="submit"
                    className="px-4"
                    disabled={loading.submission || isLocked} // [EDITED: KPI Task - Mahesh Sharma]
                    style={{
                      backgroundColor: (loading.submission || isLocked) ? "#ccc" : "#76df23", // [EDITED: KPI Task - Mahesh Sharma]
                      borderColor: (loading.submission || isLocked) ? "#ccc" : "#76df23", // [EDITED: KPI Task - Mahesh Sharma]
                    }}
                  >
                    {loading.submission ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className={`bi ${isLocked ? "bi-lock-fill" : "bi-check-circle"} me-2`}></i>
                        {isLocked ? "Submission Locked" : "Submit for Review"} {/* [EDITED: KPI Task - Mahesh Sharma] */}
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          {/* Loading Overlay - Only shown for initial data loading */}
          {(loading.clients || loading.projects) && (
            <div className="position-fixed top-50 start-50 translate-middle">
              <Spinner animation="border" variant="primary" />
            </div>
          )}
        </div>
      </main>
    </Layout>
  );
};

export default EmployeeKpi;